import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPencil, BsTrash, BsCheck, BsChevronDown, BsStars, BsPlus } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { formatEuro } from '../../../utils/formatters';
import DealProductsSection from '../DealProductsSection/DealProductsSection';
import StageTimeline from '../StageTimeline/StageTimeline';

/** Banner shown at the top of an expanded won/lost deal. */
const ClosedDealBanner = ({ status, lossReason, lossReasonNote }) => {
	if (status !== 'won' && status !== 'lost') return null;
	const isWon = status === 'won';
	return (
		<div
			className={`mt-3 mb-1 px-3 py-2 rounded-card text-[12px] ${
				isWon ? 'bg-success-200 text-success-700' : 'bg-warning-200 text-warning-700'
			}`}
		>
			<div className="flex items-center gap-2 font-semibold">
				<BsCheck className="w-4 h-4 flex-shrink-0" />
				{isWon ? 'Dieser Deal ist gewonnen.' : 'Dieser Deal ist verloren.'}
				<span className={`font-normal ${isWon ? 'text-success-700/80' : 'text-warning-700/80'}`}>
					Bearbeitung weiterhin möglich.
				</span>
			</div>
			{/* A note without a category is possible whenever the org leaves the
			    loss reason optional — show it rather than swallowing it. */}
			{!isWon && (lossReason || lossReasonNote) && (
				<div className="mt-1.5 pl-6 font-normal text-warning-700/90">
					{lossReason && (
						<>
							<span className="font-semibold">Verlustgrund:</span> {lossReason}
						</>
					)}
					{lossReasonNote && (
						<span className={`block text-warning-700/80 ${lossReason ? 'mt-0.5' : ''}`}>
							{lossReasonNote}
						</span>
					)}
				</div>
			)}
		</div>
	);
};

ClosedDealBanner.propTypes = {
	status: PropTypes.string,
	lossReason: PropTypes.string,
	lossReasonNote: PropTypes.string,
};

const DealExpandedRow = ({
	deal,
	contacts,
	stages,
	members,
	allProducts,
	allBundles,
	dealCustomDefs,
	onDealUpdate,
	onDealDelete,
	onStageTransition,
	onCreateContact,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({});
	const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
	const [quickContactOpen, setQuickContactOpen] = useState(false);
	const [quickContactDraft, setQuickContactDraft] = useState({ firstName: '', lastName: '' });
	const [quickContactSubmitting, setQuickContactSubmitting] = useState(false);

	const prob = deal.closingChance !== null ? Math.round(Number(deal.closingChance) * 100) : null;

	const getDealDecisionMaker = () => {
		const dmPivot = (deal.dealContacts ?? []).find((dc) => dc.isDecisionMaker);
		if (!dmPivot) return null;
		return contacts.find((c) => c.id === dmPivot.crmContactId) ?? null;
	};

	const startEdit = () => {
		const dm = getDealDecisionMaker();
		setDraft({
			title: deal.title ?? '',
			description: deal.description ?? '',
			dealValue: deal.dealValue?.toLocaleString('de-DE') ?? '',
			expectedCloseAt: deal.expectedCloseAt?.split('T')[0] ?? '',
			userId: deal.users?.[0]?.id ?? null,
			decisionMakerCrmContactId: dm?.id ?? '',
			_customFields: { ...(deal.customFields ?? {}) },
		});
		setEditing(true);
	};

	const saveEdit = async () => {
		const patch = {};
		if (draft.title !== (deal.title ?? '')) patch.title = draft.title || null;
		if (draft.description !== (deal.description ?? '')) patch.description = draft.description || null;
		const rawValue = draft.dealValue ? Number(String(draft.dealValue).replace(/\./g, '').replace(',', '.')) : null;
		if (rawValue !== Number(deal.dealValue ?? 0)) patch.dealValue = rawValue;
		const prevExpected = deal.expectedCloseAt?.split('T')[0] ?? '';
		if (draft.expectedCloseAt !== prevExpected) patch.expectedCloseAt = draft.expectedCloseAt || null;
		const prevUserId = deal.users?.[0]?.id ?? null;
		if (draft.userId !== prevUserId) patch.userId = draft.userId;
		const prevDm = getDealDecisionMaker()?.id ?? '';
		if (draft.decisionMakerCrmContactId !== prevDm)
			patch.decisionMakerCrmContactId = draft.decisionMakerCrmContactId || null;
		if (draft._customFields && JSON.stringify(draft._customFields) !== JSON.stringify(deal.customFields ?? {})) {
			patch.customFields = draft._customFields;
		}
		setEditing(false);
		if (Object.keys(patch).length > 0) await onDealUpdate(deal.id, patch);
	};

	const cancelEdit = () => setEditing(false);

	const handleQuickContactCreate = async () => {
		const { firstName, lastName } = quickContactDraft;
		if (!firstName?.trim() && !lastName?.trim()) return;
		setQuickContactSubmitting(true);
		try {
			const result = await onCreateContact({
				crmCompanyId: deal.crmCompanyId,
				firstName: firstName?.trim() || null,
				lastName: lastName?.trim() || null,
			});
			const newContactId = result?.contact?.id ?? result?.id;
			if (newContactId) {
				setDraft((p) => ({ ...p, decisionMakerCrmContactId: newContactId }));
			}
			setQuickContactOpen(false);
			setQuickContactDraft({ firstName: '', lastName: '' });
		} finally {
			setQuickContactSubmitting(false);
		}
	};

	const renderFieldRow = (label, viewContent, editContent, opts = {}) => (
		<div
			className={`grid gap-4 ${opts.noBorder ? '' : 'border-b border-bgBase-400'} transition-colors ${editing ? 'py-2 items-start' : 'py-2.5 items-center hover:bg-bgBase-300'}`}
			style={{ gridTemplateColumns: '180px 1fr' }}
		>
			<dt className={`text-[12px] font-medium text-bgBase-800 ${editing ? 'pt-2' : ''}`}>{label}</dt>
			<dd className="m-0 text-[13px]">{editing ? editContent : viewContent}</dd>
		</div>
	);

	const renderCustomFieldEdit = (def) => {
		const cfValue = (draft._customFields ?? deal.customFields ?? {})[def.id] ?? '';
		const handleChange = (val) => {
			const prev = draft._customFields ?? { ...(deal.customFields ?? {}) };
			setDraft((p) => ({ ...p, _customFields: { ...prev, [def.id]: val } }));
		};

		if (def.fieldType === 'checkbox') {
			return (
				<div className="flex items-center h-[34px]">
					<Checkbox id={`deal-custom-field-${def.id}`} checked={Boolean(cfValue)} onChange={handleChange} />
				</div>
			);
		}
		if (def.fieldType === 'select') {
			return (
				<Select value={cfValue} onChange={(e) => handleChange(e.target.value)} className="cursor-pointer">
					<option value="">–</option>
					{(def.selectOptions ?? []).map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</Select>
			);
		}
		if (def.fieldType === 'textarea') {
			return <Textarea value={cfValue} onChange={(e) => handleChange(e.target.value)} rows={2} placeholder="—" />;
		}
		return (
			<TextInput
				type={def.fieldType === 'number' ? 'number' : def.fieldType === 'date' ? 'date' : 'text'}
				value={cfValue}
				onChange={(e) => handleChange(e.target.value)}
			/>
		);
	};

	const displayCustomFieldValue = (def) => {
		const cfVal = (deal.customFields ?? {})[def.id];
		if (cfVal === null || cfVal === '') return null;
		if (def.fieldType === 'checkbox') return cfVal ? 'Ja' : 'Nein';
		if (def.fieldType === 'date') {
			try {
				return new Intl.DateTimeFormat('de-DE').format(new Date(cfVal));
			} catch {
				return String(cfVal);
			}
		}
		return String(cfVal);
	};

	return (
		<div className="px-4 pb-4 bg-white border-t border-bgBase-500">
			<ClosedDealBanner status={deal.status} lossReason={deal.lossReason} lossReasonNote={deal.lossReasonNote} />

			<div className="flex items-center justify-end gap-1 py-2.5 border-b border-bgBase-500 mb-3">
				{!editing ? (
					<>
						<Button type="button" variant="outline" size="sm" onClick={startEdit}>
							<BsPencil className="w-3 h-3" /> Bearbeiten
						</Button>
						<Button type="button" variant="danger" size="sm" onClick={() => onDealDelete(deal)}>
							<BsTrash className="w-3 h-3" /> Löschen
						</Button>
					</>
				) : (
					<>
						<Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
							Abbrechen
						</Button>
						<Button type="button" variant="solid" size="sm" onClick={saveEdit}>
							<BsCheck className="w-3.5 h-3.5" /> Speichern
						</Button>
					</>
				)}
			</div>

			<div className="flex gap-8 flex-wrap pb-3.5 mb-3.5 border-b border-bgBase-500">
				<div className="flex flex-col gap-0.5">
					<span className="text-xs-head text-bgBase-800">Dealwert</span>
					<span className="text-md-bold text-text tabular-nums">{formatEuro(deal.dealValue)}</span>
				</div>
				<div className="flex flex-col gap-0.5">
					<span className="text-xs-head text-bgBase-800 inline-flex items-center gap-1">
						Wahrscheinlichkeit <BsStars className="w-2.5 h-2.5 text-primary-400" />
					</span>
					<span className="text-md-bold text-text tabular-nums">{prob !== null ? `${prob}%` : '–'}</span>
				</div>
				<div className="flex flex-col gap-0.5">
					<span className="text-xs-head text-bgBase-800">Erwarteter Abschluss</span>
					<span className="text-md-bold text-text">
						{deal.expectedCloseAt
							? new Intl.DateTimeFormat('de-DE', {
									day: '2-digit',
									month: 'short',
									year: 'numeric',
								}).format(new Date(deal.expectedCloseAt))
							: '–'}
					</span>
				</div>
				<div className="flex flex-col gap-0.5">
					<span className="text-xs-head text-bgBase-800">Owner</span>
					<Popover
						open={ownerDropdownOpen}
						onClose={() => setOwnerDropdownOpen(false)}
						size="s"
						align="start"
						trigger={
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setOwnerDropdownOpen(!ownerDropdownOpen);
								}}
								className="text-md-bold text-text flex items-center gap-2 hover:text-primary-600 transition-colors"
								title="Owner ändern"
							>
								{deal.users?.[0] ? (
									<>
										<span className="w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center text-[9px] font-semibold">
											{(deal.users[0].fullName ?? '?')[0]?.toUpperCase()}
										</span>
										{deal.users[0].fullName?.split(' ')[0] ?? '–'}
									</>
								) : (
									<span className="text-bgBase-800 text-sm">Zuweisen</span>
								)}
								<BsChevronDown className="w-2.5 h-2.5 text-bgBase-800" />
							</button>
						}
					>
						<Panel.Body padded={false} className="max-h-48">
							<Menu>
								<Menu.Label>Owner zuweisen</Menu.Label>
								{members.map((m) => {
									const isAssigned = (deal.users ?? []).some((u) => u.id === m.userId);
									return (
										<Menu.Item
											key={m.userId}
											selected={isAssigned}
											onClick={() => {
												onDealUpdate(deal.id, { userId: isAssigned ? null : m.userId });
												setOwnerDropdownOpen(false);
											}}
											trailing={
												isAssigned ? (
													<BsCheck className="w-3.5 h-3.5 text-primary-500" />
												) : undefined
											}
										>
											<span className="flex items-center gap-2">
												<span className="w-5 h-5 rounded-full bg-primary-500 text-white flex items-center justify-center text-[8px] font-semibold flex-shrink-0">
													{(m.fullName ?? m.email ?? '?')[0]?.toUpperCase()}
												</span>
												<span className="truncate">{m.fullName ?? m.email}</span>
											</span>
										</Menu.Item>
									);
								})}
								{(deal.users?.length ?? 0) > 0 && (
									<>
										<Menu.Separator />
										<Menu.Item
											onClick={() => {
												onDealUpdate(deal.id, { userId: null });
												setOwnerDropdownOpen(false);
											}}
										>
											Zuweisung entfernen
										</Menu.Item>
									</>
								)}
							</Menu>
						</Panel.Body>
					</Popover>
				</div>
			</div>

			{/* Stage timeline — days per stage (Pipedrive-style) */}
			<StageTimeline
				stages={stages}
				currentStageId={deal.stageId}
				stageHistory={deal.stageHistory}
				createdAt={deal.createdAt}
				onTransition={(stageId) => onStageTransition(deal.id, stageId)}
			/>

			<dl
				className={`mt-4 transition-colors ${editing ? 'border border-primary-300 rounded-card px-3.5 ring-2 ring-primary-100' : 'overflow-hidden'}`}
			>
				{renderFieldRow(
					'Titel',
					<span className="text-text font-medium flex justify-end">{deal.title || '–'}</span>,
					<TextInput
						value={draft.title}
						onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
					/>,
				)}
				{renderFieldRow(
					'Beschreibung',
					<span
						className={`flex justify-end text-right ${deal.description ? 'text-text' : 'text-bgBase-800'}`}
						style={{ textWrap: 'pretty' }}
					>
						{deal.description || '–'}
					</span>,
					<Textarea
						value={draft.description}
						onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
						rows={2}
						placeholder="—"
					/>,
				)}
				{renderFieldRow(
					'Wert',
					<span className="text-text font-semibold tabular-nums flex justify-end">
						{formatEuro(deal.dealValue)}
					</span>,
					<TextInput
						value={draft.dealValue}
						onChange={(e) => setDraft((p) => ({ ...p, dealValue: e.target.value }))}
						placeholder="0"
					/>,
				)}
				{renderFieldRow(
					'Erwarteter Abschluss',
					<span className="text-text flex justify-end">
						{deal.expectedCloseAt
							? new Intl.DateTimeFormat('de-DE', {
									day: '2-digit',
									month: 'short',
									year: 'numeric',
								}).format(new Date(deal.expectedCloseAt))
							: '–'}
					</span>,
					<TextInput
						type="date"
						value={draft.expectedCloseAt}
						onChange={(e) => setDraft((p) => ({ ...p, expectedCloseAt: e.target.value }))}
						className="cursor-pointer"
					/>,
				)}

				{/* Probability — always read-only */}
				<div
					className="grid gap-4 py-2.5 items-center border-b border-bgBase-400 hover:bg-bgBase-300 transition-colors"
					style={{ gridTemplateColumns: '180px 1fr' }}
				>
					<dt className="text-[12px] font-medium text-bgBase-800">Wahrscheinlichkeit</dt>
					<dd className="m-0 text-[13px] flex items-center justify-end gap-1.5">
						{editing ? (
							<span className="text-bgBase-800 text-[12px] inline-flex items-center gap-1">
								<BsStars className="w-3 h-3 text-primary-400" />
								{prob !== null ? `${prob}%` : '–'} · wird automatisch von der KI berechnet
							</span>
						) : (
							<>
								<span className="text-text font-semibold tabular-nums">
									{prob !== null ? `${prob}%` : '–'}
								</span>
								<BsStars className="w-3 h-3 text-primary-400" title="KI-berechnet" />
							</>
						)}
					</dd>
				</div>

				{renderFieldRow(
					'Entscheidungsträger',
					(() => {
						const dm = getDealDecisionMaker();
						return dm ? (
							<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-200 text-primary-500 text-[11px] font-semibold rounded-full ml-auto">
								<BsCheck className="w-3 h-3" />
								{dm.fullName ??
									[dm.firstName, dm.lastName].filter(Boolean).join(' ') ??
									dm.emailAddress}
							</span>
						) : (
							<span className="text-bgBase-800 flex justify-end">–</span>
						);
					})(),
					<div className="flex items-start gap-1.5 relative">
						<Select
							value={draft.decisionMakerCrmContactId ?? ''}
							onChange={(e) => setDraft((p) => ({ ...p, decisionMakerCrmContactId: e.target.value }))}
							className="cursor-pointer flex-1"
						>
							<option value="">— wählen —</option>
							{contacts
								.filter((c) => c.crmCompanyId === deal.crmCompanyId)
								.map((c) => (
									<option key={c.id} value={c.id}>
										{c.fullName ??
											`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ??
											c.emailAddress}
									</option>
								))}
						</Select>
						<Popover
							open={quickContactOpen}
							onClose={() => setQuickContactOpen(false)}
							size="s"
							align="end"
							trigger={
								<IconButton
									icon={BsPlus}
									label="Neuen Kontakt anlegen"
									onClick={() => setQuickContactOpen((p) => !p)}
									className="h-10 w-10"
								/>
							}
						>
							<Panel.Header>Schnell-Kontakt anlegen</Panel.Header>
							<Panel.Body className="flex flex-col gap-2">
								<TextInput
									placeholder="Vorname"
									value={quickContactDraft.firstName}
									onChange={(e) => setQuickContactDraft((p) => ({ ...p, firstName: e.target.value }))}
									autoFocus
								/>
								<TextInput
									placeholder="Nachname"
									value={quickContactDraft.lastName}
									onChange={(e) => setQuickContactDraft((p) => ({ ...p, lastName: e.target.value }))}
									onKeyDown={(e) => {
										if (e.key === 'Enter') handleQuickContactCreate();
									}}
								/>
							</Panel.Body>
							<Panel.Footer className="justify-end">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => {
										setQuickContactOpen(false);
										setQuickContactDraft({ firstName: '', lastName: '' });
									}}
								>
									Abbrechen
								</Button>
								<Button
									type="button"
									variant="solid"
									size="sm"
									onClick={handleQuickContactCreate}
									disabled={
										(!quickContactDraft.firstName?.trim() && !quickContactDraft.lastName?.trim()) ||
										quickContactSubmitting
									}
									loading={quickContactSubmitting}
								>
									Anlegen
								</Button>
							</Panel.Footer>
						</Popover>
					</div>,
					{ noBorder: dealCustomDefs.length === 0 },
				)}

				{dealCustomDefs.map((def) => {
					const cfDisplay = displayCustomFieldValue(def);
					return (
						<div
							key={def.id}
							className={`grid gap-4 border-t border-bgBase-400 transition-colors ${editing ? 'py-2 items-start' : 'py-2.5 items-center hover:bg-bgBase-300'}`}
							style={{ gridTemplateColumns: '180px 1fr' }}
						>
							<dt className={`text-[12px] font-medium text-bgBase-800 ${editing ? 'pt-2' : ''}`}>
								{def.name}
							</dt>
							<dd className="m-0 text-[13px]">
								{editing ? (
									renderCustomFieldEdit(def)
								) : def.fieldType === 'url' && cfDisplay ? (
									<a
										href={
											((deal.customFields ?? {})[def.id] ?? '').startsWith('http')
												? (deal.customFields ?? {})[def.id]
												: `https://${(deal.customFields ?? {})[def.id]}`
										}
										target="_blank"
										rel="noopener noreferrer"
										className="text-primary-600 hover:text-primary-700 font-medium flex justify-end hover:underline"
									>
										{cfDisplay}
									</a>
								) : (
									<span
										className={`flex justify-end text-right ${cfDisplay ? 'text-text font-medium' : 'text-bgBase-800'}`}
									>
										{cfDisplay || '–'}
									</span>
								)}
							</dd>
						</div>
					);
				})}
			</dl>

			<DealProductsSection
				deal={deal}
				allProducts={allProducts}
				allBundles={allBundles}
				onUpdate={(products) => onDealUpdate(deal.id, { products })}
			/>
		</div>
	);
};

DealExpandedRow.propTypes = {
	deal: PropTypes.object.isRequired,
	contacts: PropTypes.array.isRequired,
	stages: PropTypes.array.isRequired,
	members: PropTypes.array.isRequired,
	allProducts: PropTypes.array.isRequired,
	allBundles: PropTypes.array.isRequired,
	dealCustomDefs: PropTypes.array.isRequired,
	onDealUpdate: PropTypes.func.isRequired,
	onDealDelete: PropTypes.func.isRequired,
	onStageTransition: PropTypes.func.isRequired,
	onCreateContact: PropTypes.func.isRequired,
};

export default DealExpandedRow;
